import { useRef, useMemo, useState, useEffect } from "react";
import { CodeEditor } from "./CodeEditor";
import { XmlOutput } from "./XmlOutput";
import { useSplitResize } from "../../hooks/useSplitResize";
import type { ConvertResponse, BatchConvertResponse } from "../../types/sigwaz";

interface Props {
  yaml: string;
  setYaml: (v: string) => void;
  result: ConvertResponse | null;
  batchResult: BatchConvertResponse | null;
  ruleIdStart: number;
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 60);
}

function mostCommon(arr: string[]): string {
  if (!arr.length) return "";
  const counts: Record<string, number> = {};
  for (const v of arr) counts[v] = (counts[v] || 0) + 1;
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
}

export function SingleConverter({ yaml, setYaml, result, batchResult, ruleIdStart }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { split, onDividerMouseDown } = useSplitResize(containerRef, 50);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const yamlSlug = useMemo(() => {
    const isMultiDoc =
      /^---[ \t]*$/m.test(yaml) || (yaml.match(/^title[ \t]*:/img) || []).length > 1;
    if (isMultiDoc) {
      const products   = [...yaml.matchAll(/^[ \t]*product[ \t]*:[ \t]*(\S+)/gim)].map(m => m[1].trim());
      const categories = [...yaml.matchAll(/^[ \t]*category[ \t]*:[ \t]*(\S+)/gim)].map(m => m[1].trim());
      const top = [mostCommon(products), mostCommon(categories)].filter(Boolean);
      return top.length ? slugify(top.join("_")) : "sigma_batch";
    }
    const m = yaml.match(/^title[ \t]*:[ \t]*(.+)$/im);
    return m ? slugify(m[1].trim()) : "";
  }, [yaml]);

  return (
    <div className="main-area" ref={containerRef}>
      <div
        className="split"
        style={isMobile ? undefined : { gridTemplateColumns: `${split}% 5px ${100 - split}%` }}
      >
        <div className="split-panel">
          <CodeEditor value={yaml} onChange={setYaml} onClear={() => setYaml("")} yamlSlug={yamlSlug} />
        </div>

        <div className="resizer" onMouseDown={onDividerMouseDown} />

        <div className="split-panel">
          <XmlOutput
            result={result?.result ?? null}
            batchXml={batchResult?.merged_xml || null}
            batchXmlFiles={batchResult?.xml_files ?? []}
            batchCount={batchResult?.total_rules ?? null}
            batchErrors={batchResult?.total_errors ?? null}
            ruleIdStart={ruleIdStart}
            yamlSlug={yamlSlug}
          />
        </div>
      </div>
    </div>
  );
}
